"use client";

import { useState } from "react";
import { Check, FileText, Info, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const legalSections = [{
  id: "acceptance-eligibility",
  icon: FileText,
  title: "Acceptance, Eligibility & Services",
  items: [{
    heading: "1. Acceptance of Terms",
    content: "By accessing or using the website ironpeakmenshealth.com, scheduling a consultation, or receiving telemedicine services through IronPeak Men's Health ('we,' 'our,' or 'the Clinic'), you agree to these Terms of Service ('Terms'). If you do not agree, please discontinue use of this site and our services."
  }, {
    heading: "2. Nature of Services",
    content: "IronPeak Men's Health is a telemedicine-based medical practice founded in Prosper, Texas. Our licensed healthcare providers deliver consultations, evaluations, and treatments remotely to patients located within the State of Texas. We do not provide emergency medical services. If you are experiencing a medical emergency, call 911 or go to your nearest emergency department."
  }, {
    heading: "3. Eligibility",
    content: "You must: Be 18 years of age or older; Be physically located in Texas at the time of your telemedicine visit; and Provide accurate, current, and complete information during registration and treatment."
  }, {
    heading: "4. Medical Disclaimer",
    content: "All content on this website—including text, graphics, and other material—is provided for educational and informational purposes only. It is not a substitute for professional medical advice, diagnosis, or treatment. Do not disregard professional medical advice or delay seeking it because of something you have read on this website. Always consult a qualified healthcare professional for questions regarding a medical condition."
  }]
}, {
  id: "telemedicine-fees",
  icon: Shield,
  title: "Telemedicine, Fees & Prescriptions",
  items: [{
    heading: "5. Telemedicine Consent",
    content: "By receiving care through IronPeak Men's Health, you acknowledge that you have read, understood, and agreed to our Telemedicine Informed Consent, which outlines: The nature, benefits, and risks of telemedicine; Prescription and treatment limitations; Financial responsibility; Confidentiality and data protection; and Your rights as a patient. The Telemedicine Informed Consent is incorporated into these Terms by reference."
  }, {
    heading: "6. Fees, Payment, and Refund Policy",
    content: "IronPeak Men's Health is a self-pay clinic and does not accept insurance. All payments are due at the time of service unless otherwise agreed in writing. If you wish to submit your visit documentation to your insurance provider for possible reimbursement, we can provide copies of: Visit summaries, Lab orders and results, and Prescriptions issued on your behalf. Services rendered and medications dispensed are generally non-refundable. Refunds, if applicable, are determined according to our Cancellation and Refund Policy, available upon request."
  }, {
    heading: "7. Prescriptions and Treatment Limitations",
    content: "Medications are prescribed only when medically appropriate and in accordance with all applicable laws, including: The Texas Medical Board's telemedicine rules, and The Ryan Haight Online Pharmacy Consumer Protection Act. We reserve the right to refuse or discontinue any medication, treatment, or service if deemed unsafe, inappropriate, or non-compliant with medical or legal standards."
  }, {
    heading: "8. Privacy and Confidentiality",
    content: "We comply fully with the Health Insurance Portability and Accountability Act (HIPAA) and all applicable Texas privacy laws. Personal health information is used and disclosed only for purposes of providing treatment, obtaining payment, and healthcare operations, unless otherwise authorized by you or required by law. For additional details, please review our Privacy Policy."
  }]
}, {
  id: "communications-liability",
  icon: Info,
  title: "Communications, Liability & Property",
  items: [{
    heading: "9. Communications and Electronic Consent",
    content: "By using our telemedicine platform, you consent to receive communications—including appointment reminders, test results, or follow-up information—via secure electronic methods. For convenience, you may also receive limited communications by phone, text message, or email. If you prefer to restrict these methods, please notify us in writing."
  }, {
    heading: "10. Limitation of Liability",
    content: "To the maximum extent permitted by Texas law, IronPeak Men's Health, its physicians, affiliates, and employees shall not be liable for: Any indirect, incidental, consequential, or special damages; Any loss arising from website use, telemedicine software, or third-party platform errors; or Interruptions, delays, or security breaches in telecommunication systems. You agree that your sole remedy for dissatisfaction with our website or services is to discontinue their use."
  }, {
    heading: "11. Intellectual Property",
    content: "All website content—including text, graphics, design, logos, and trademarks—is the exclusive property of IronPeak Men's Health or its licensors and is protected under U.S. and Texas intellectual property laws. You may not reproduce, distribute, or modify any material without written consent."
  }, {
    heading: "12. Website Availability and Third-Party Links",
    content: "We do not guarantee that our website or telemedicine platform will always be available, uninterrupted, or error-free. Our site may contain links to third-party websites; these are provided for convenience and do not imply endorsement or control. You assume all risk when visiting or relying on third-party sites."
  }]
}, {
  id: "legal-modifications",
  icon: Shield,
  title: "Governing Law, Modifications & Contact",
  items: [{
    heading: "13. Governing Law and Jurisdiction",
    content: "These Terms and any dispute related to IronPeak Men's Health shall be governed by and construed under the laws of the State of Texas, without regard to its conflict-of-law principles. The exclusive jurisdiction for any disputes shall be the state or federal courts located in Collin County, Texas."
  }, {
    heading: "14. Modification of Terms",
    content: "We may update or modify these Terms at any time without prior notice. The date of the most recent revision will appear at the top of this page. Continued use of our website or services after changes are posted constitutes acceptance of those changes."
  }, {
    heading: "15. Contact Information",
    content: <div className="space-y-2">
            <div>IronPeak Men's Health</div>
            <div>Prosper, Texas</div>
            <div>Serving Patients Statewide via Telemedicine</div>
            <div>Email: hello@ironpeakmenshealth.com</div>
            <div>Phone: (469) 557-5547</div>
            <div>Website: ironpeakmenshealth.com</div>
          </div>
  }]
}];
function LegalCard({
  section,
  index
}: {
  section: typeof legalSections[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  const Icon = section.icon;
  return <div ref={ref} data-index={index} className="transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(2rem)",
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="bg-[#F9FAFB] shadow-md border-border hover:shadow-medium">
        <CardHeader className="border-b border-border pb-4">
          <div className="flex items-center gap-3">
            <Icon className="size-5 text-secondary shrink-0" />
            <CardTitle className="text-lg font-semibold text-foreground">
              {section.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <Accordion type="single" collapsible className="space-y-0">
            {section.items.map((item, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i}>
                <AccordionTrigger className="text-foreground hover:text-primary py-3 text-base font-medium">
                  {item.heading}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {item.content}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </CardContent>
      </Card>
    </div>;
}
export default function OurLegalTerms() {
  const [accepted, setAccepted] = useState(false);
  return <section id="legal-terms" className="relative bg-[#0F172A] py-16 min-h-screen">
      <div className="container max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="size-8 text-secondary" />
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground-dark">
              Terms of Service
            </h2>
          </div>
          <p className="text-muted-foreground-dark text-base mb-2">
            Effective Date: November 1st, 2025
          </p>
          <p className="text-base md:text-lg text-white max-w-2xl mx-auto">
            Founded in Prosper, Texas — Serving Men Across the Lone Star State
          </p>
          <p className="text-sm md:text-base text-white">
            Please review our terms carefully before using our services
          </p>
        </div>

        <div className="space-y-6 mb-24">
          {legalSections.map((section, index) => <LegalCard key={section.id} section={section} index={index} />)}
        </div>

        <div className="text-center text-muted-foreground-dark text-sm pb-16">
          <p>© 2025 IronPeak Men's Health. All Rights Reserved.</p>
        </div>
      </div>

      
    </section>;
}